import type { AuthUser, AuthVesselOption } from "../types/auth";
import type { VesselId } from "../types/vessel";
import { normalizeVesselId } from "./vesselIds";

const OFFICE_VESSEL_FILTERABLE_PATHS = [
  "/dashboard",
  "/requisitions",
  "/rfqs",
  "/purchase-orders",
  "/budget-residuals",
  "/budget/approvals",
  "/projects",
  "/contracts",
  "/invoices",
  "/reports",
  "/credit-notes",
  "/grns",
  "/disputes",
  "/inventory",
  "/item-history",
] as const;

export const OFFICE_VESSEL_SCOPE_ALL_LABEL = "All Vessels";

function dedupeVesselOptions(vessels: AuthVesselOption[]): AuthVesselOption[] {
  const seenIds = new Set<string>();
  const options: AuthVesselOption[] = [];

  for (const vessel of vessels) {
    const normalizedId = normalizeVesselId(vessel.id);
    if (normalizedId === null) {
      continue;
    }
    const dedupeKey = String(normalizedId);
    if (seenIds.has(dedupeKey)) {
      continue;
    }
    seenIds.add(dedupeKey);
    options.push({
      id: normalizedId,
      vessel_name: vessel.vessel_name,
      vessel_code: vessel.vessel_code,
    });
  }

  return options;
}

export function getOfficeVesselOptions(user: AuthUser | null | undefined): AuthVesselOption[] {
  if (user?.type !== "office") {
    return [];
  }

  const scopedVessels = dedupeVesselOptions(user.vessels ?? []);
  if (scopedVessels.length > 0) {
    return scopedVessels;
  }

  const fallbackVesselId = normalizeVesselId(user.vessel_id);
  if (fallbackVesselId === null) {
    return [];
  }

  return [
    {
      id: fallbackVesselId,
      vessel_name: user.vessel_name?.trim() || "Assigned Vessel",
      vessel_code: user.vessel_code?.trim() || "",
    },
  ];
}

export function formatOfficeVesselOptionLabel(vessel: AuthVesselOption): string {
  const vesselCode = vessel.vessel_code.trim();
  return vesselCode ? `${vesselCode} - ${vessel.vessel_name}` : vessel.vessel_name;
}

export function isOfficeVesselScopedPath(pathname: string): boolean {
  if (["/budget", "/budget/plan", "/budget/drydock", "/budget/predelivery", "/budget/close-year"].includes(pathname)) return true;
  return OFFICE_VESSEL_FILTERABLE_PATHS.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function applyOfficeVesselScopeToSearchParams(
  searchParams: globalThis.URLSearchParams,
  vesselId: VesselId | null,
): globalThis.URLSearchParams {
  const nextSearchParams = new globalThis.URLSearchParams(searchParams);
  nextSearchParams.delete("vessel_id");
  nextSearchParams.delete("page");
  nextSearchParams.delete("start_row");
  if (vesselId !== null) {
    nextSearchParams.set("vessel_id", String(vesselId));
  }
  return nextSearchParams;
}

export function applyOfficeVesselScopeToHref(to: string, vesselId: VesselId | null): string {
  if (vesselId === null) {
    return to;
  }

  const [pathWithQuery, hashFragment] = to.split("#", 2);
  const [pathname, queryString = ""] = pathWithQuery.split("?", 2);
  const searchParams = applyOfficeVesselScopeToSearchParams(
    new globalThis.URLSearchParams(queryString),
    vesselId,
  );
  const nextQuery = searchParams.toString();

  return `${pathname}${nextQuery ? `?${nextQuery}` : ""}${hashFragment ? `#${hashFragment}` : ""}`;
}
